import { PaddlePrice } from 'components/dialogs/payment/model'
import React, { useEffect, createContext, useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import Logger from 'services/Logger'

import { logPurchasesAnalytics } from 'components/dialogs/payment/PlansInfoDialog'
import { EventProperties, Events } from 'analytics/Analytics'
import { updateAccountAfterPlanSubscription } from 'services/api/payments'
import { Account } from 'services/db/Queries/AccountQueries'
import CircularProgress from '@mui/material/CircularProgress'
import Backdrop from '@mui/material/Backdrop'
import { highestActiveTier, Tier } from 'utils/api/accounts'
import { useNotification } from './NotificationProvider'
import { t } from 'i18next'

interface PromotionsContextProps {
  purchaseStatus: boolean
}

interface PromotionsProviderProps {
  children: React.ReactNode
}

const PromotionsContext = createContext<PromotionsContextProps | undefined>(
  undefined
)

export const usePromotions = () => {
  const context = useContext(PromotionsContext)
  if (!context) {
    throw new Error('usePromotions must be used within a PromotionsProvider')
  }
  return context
}

function PromotionsProvider(props: PromotionsProviderProps) {
  const location = useLocation()
  const [purchaseStatus, setPurchaseStatus] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { showNotification } = useNotification()

  useEffect(() => {
    Logger.blue('PROMO:Location', location)
    if (location.pathname.includes('initiatepurchase')) {
      const purchaseParams = new URLSearchParams(location.search)

      const coupon = purchaseParams.get('coupon')
      const subscriptionID = purchaseParams.get('subscriptionID')
      const androidOfferId = purchaseParams.get('androidOfferID')
      const fromWhichPlace = purchaseParams.get('fromWhichPlace')
      const promoID = purchaseParams.get('promoID')

      Logger.blue('PROMO:Coupon', coupon)
      Logger.blue('PROMO:subscriptionID', subscriptionID)

      navigate('/', { replace: true })

      if (coupon && subscriptionID) {
        setTimeout(() => {
          Account().then((account) => {
            const generalAnalyticsData = {
              [EventProperties.CATEGORY]: 'In_App_Purchase',
              'Subscription ID': subscriptionID,
              AndroidOfferID: androidOfferId,
              'Is Pro User?':
                highestActiveTier(account) !== Tier.FREE ? 'Yes' : 'No',
              'From which place': fromWhichPlace,
              'Purchase screen type': 'No purchase screen',
              'Promo ID': promoID
            }

            window.Paddle?.Product?.Prices(
              subscriptionID,
              function (price: PaddlePrice) {
                Logger.red('PROMO:PaddleProduct', price)

                logPurchasesAnalytics(Events.CLICK_INAPP_PURCHASE, account, {
                  ...generalAnalyticsData
                })

                if (highestActiveTier(account) !== Tier.FREE) {
                  showNotification(
                    t('alreadyPurchased', { appName: t('appName') }),
                    'warning'
                  )
                  return
                }

                // If the price is valid, initiate the purchase
                window?.Paddle?.Checkout.open({
                  product: subscriptionID,
                  coupon: coupon,

                  successCallback: (e: any) => {
                    window?.Paddle?.Order?.details(
                      e.checkout.id,
                      (data: {
                        state: string
                        order: { subscription_id: any }
                      }) => {
                        if (account) {
                          if (data.state === 'processed') {
                            updateAccountAfterPlanSubscription(account, {
                              ...e,
                              checkout: {
                                ...e.checkout,
                                subscriptionId: data.order.subscription_id
                              }
                            }).then((updatedAccount) => {
                              logPurchasesAnalytics(
                                Events.INAPP_PURCHASED,
                                updatedAccount,
                                {
                                  ...generalAnalyticsData
                                }
                              )
                              // Clean the user URL

                              setTimeout(() => {
                                setLoading(false)
                              }, 2000)
                            })
                          } else {
                            showNotification(t('promoExpired'), 'error')
                          }
                        }
                      },
                      false
                    )
                  }
                })
              }
            )
          })
        }, 3000)
      }
    }
  }, [location.search])

  return (
    <PromotionsContext.Provider value={{ purchaseStatus }}>
      {props.children}
      <Backdrop open={loading} style={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </PromotionsContext.Provider>
  )
}

export default PromotionsProvider
